// Die Verbindung zum Server: eine kleine Hülle um fetch. Jede App spricht
// nur mit ihrer eigenen Adresse (/api/…); Cookies gehen von selbst mit.
//
// Seit E2 (draußen ist ein Funkloch der Normalfall):
//  - Ohne Netz (`navigator.onLine === false`) geht gar keine Anfrage hinaus –
//    die App weiß sofort Bescheid, statt auf eine Zeitüberschreitung zu warten.
//  - Jede Anfrage hat ein Zeitlimit: 20 s für JSON, 120 s für Dateien.
//  - Antworten, die kein JSON sind (Caddy schickt bei 502 oder 413 Text, ein
//    WLAN-Anmeldeportal eine ganze Seite), werden zu `ApiFehler` mit Status –
//    nie zu einem „SyntaxError“ und nie zu einem scheinbaren Erfolg.
//
// Seit E2, Schritt 4 holt die App auch Dateien zurück (`dateiHolen`: Fotos,
// Memos und das K2-PDF zum Nachsehen) – mit denselben Regeln. Seit E3 auch
// PATCH (`aendern`) fürs Berichtigen im Büro.

import type { Abweisungsgrund, EintragAbweisung } from "@happy-energy/kern";

/** Zeitlimit für Anfragen mit JSON. */
export const ZEITLIMIT_JSON = 20_000;
/** Zeitlimit für Dateien (Fotos, Memos, PDF) – bei schwachem Empfang dauert das. */
export const ZEITLIMIT_DATEI = 120_000;

export class ApiFehler extends Error {
  readonly status: number;
  /** Warum der Server abweist: vom Wächter (Anmeldung) oder von der Annahme eines Eintrags. */
  readonly grund: Abweisungsgrund | EintragAbweisung | null;
  readonly daten: Record<string, unknown>;
  /** Die Antwort war kein JSON (Text von Caddy, Seite eines Anmeldeportals). */
  readonly keinJson: boolean;
  constructor(status: number, daten: Record<string, unknown>, keinJson = false) {
    super(typeof daten["meldung"] === "string" ? daten["meldung"] : typeof daten["message"] === "string" ? daten["message"] : `Fehler ${status}`);
    this.name = "ApiFehler";
    this.status = status;
    this.grund = typeof daten["grund"] === "string" ? (daten["grund"] as Abweisungsgrund | EintragAbweisung) : null;
    this.daten = daten;
    this.keinJson = keinJson;
  }
}

/** Kein Netz, Server nicht erreichbar oder zu langsam – draußen der Normalfall. */
export class OhneVerbindung extends Error {
  /** Die Anfrage ging hinaus, aber die Antwort kam nicht rechtzeitig. */
  readonly zeitueberschreitung: boolean;
  constructor(zeitueberschreitung = false) {
    super(zeitueberschreitung ? "Der Server hat nicht rechtzeitig geantwortet." : "Keine Verbindung zum Server.");
    this.name = "OhneVerbindung";
    this.zeitueberschreitung = zeitueberschreitung;
  }
}

/** Meldet das Gerät selbst, dass es kein Netz hat (Flugmodus, Keller)? */
export function ohneNetz(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function zeitlimit(ms: number): AbortSignal | undefined {
  return typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(ms) : undefined;
}

function istZeitueberschreitung(fehler: unknown): boolean {
  return fehler instanceof DOMException && (fehler.name === "TimeoutError" || fehler.name === "AbortError");
}

/** Schickt die Anfrage und liest die Antwort als JSON – mit den Regeln oben. */
async function abschicken<T>(pfad: string, init: RequestInit, ms: number): Promise<T> {
  if (ohneNetz()) throw new OhneVerbindung();
  let antwort: Response;
  let text: string;
  try {
    const signal = zeitlimit(ms);
    antwort = await fetch(pfad, { ...init, credentials: "same-origin", ...(signal ? { signal } : {}) });
    // Auch das Lesen der Antwort kann im Funkloch hängen bleiben – das Zeitlimit gilt mit.
    text = await antwort.text();
  } catch (fehler) {
    throw new OhneVerbindung(istZeitueberschreitung(fehler));
  }

  const { daten, keinJson } = jsonLesen(text);
  // Kein JSON ist nie ein Erfolg – auch nicht mit Status 200 (Anmeldeportal im WLAN).
  if (!antwort.ok || keinJson) throw fehlerAus(antwort.status, daten, keinJson);
  return daten as T;
}

/** Liest Text als JSON – leer ist kein Fehler (null), alles andere, was kein JSON ist, schon. */
function jsonLesen(text: string): { daten: unknown; keinJson: boolean } {
  if (!text) return { daten: null, keinJson: false };
  try {
    return { daten: JSON.parse(text), keinJson: false };
  } catch {
    return { daten: null, keinJson: true };
  }
}

function fehlerAus(status: number, daten: unknown, keinJson: boolean): ApiFehler {
  return new ApiFehler(status, (daten && typeof daten === "object" ? daten : {}) as Record<string, unknown>, keinJson);
}

export async function anfrage<T>(methode: "GET" | "POST" | "PATCH", pfad: string, body?: unknown): Promise<T> {
  return abschicken<T>(
    pfad,
    {
      method: methode,
      headers: body === undefined ? {} : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    ZEITLIMIT_JSON,
  );
}

export const holen = <T>(pfad: string) => anfrage<T>("GET", pfad);
export const senden = <T>(pfad: string, body: unknown = {}) => anfrage<T>("POST", pfad, body);
/**
 * Etwas berichtigen (PATCH, seit E3 – Kunde, Objekt, Anlage im CRM): nur,
 * was mitkommt, ändert sich. Dieselben Regeln wie `senden`.
 */
export const aendern = <T>(pfad: string, body: unknown = {}) => anfrage<T>("PATCH", pfad, body);

/**
 * Eine Datei roh hochladen (PUT) – Fotos, Memos und das K2-PDF (ab E2,
 * Schritt 2). Die Art der Datei steht im Blob (`type`) und geht als
 * content-type mit.
 */
export async function dateiSenden<T>(pfad: string, datei: Blob): Promise<T> {
  return abschicken<T>(
    pfad,
    { method: "PUT", headers: { "content-type": datei.type || "application/octet-stream" }, body: datei },
    ZEITLIMIT_DATEI,
  );
}

/**
 * Eine Datei roh holen (GET) – ein Foto, Memo oder PDF zum Nachsehen (seit
 * E2, Schritt 4). Dieselben Regeln wie oben: ohne Netz gar keine Anfrage,
 * 120 s Zeitlimit, ein Fehler mit Status als `ApiFehler`. Eine HTML-Seite
 * mit Status 200 ist das Anmeldeportal eines WLANs, nie die Datei – auch sie
 * wird zum Fehler. Der Blob trägt den content-type der Antwort.
 */
export async function dateiHolen(pfad: string): Promise<Blob> {
  if (ohneNetz()) throw new OhneVerbindung();
  let antwort: Response;
  let inhalt: Blob;
  try {
    const signal = zeitlimit(ZEITLIMIT_DATEI);
    antwort = await fetch(pfad, { method: "GET", credentials: "same-origin", ...(signal ? { signal } : {}) });
    // Auch das Lesen kann im Funkloch hängen bleiben – das Zeitlimit gilt mit.
    inhalt = await antwort.blob();
  } catch (fehler) {
    throw new OhneVerbindung(istZeitueberschreitung(fehler));
  }
  const seite = /^text\/html/i.test(antwort.headers.get("content-type") ?? "");
  if (!antwort.ok || seite) {
    const { daten, keinJson } = jsonLesen(await inhalt.text());
    throw fehlerAus(antwort.status, daten, keinJson || seite);
  }
  return inhalt;
}
